// The map's layer switches (Layers sheet), kept on this device under valor-web:map-layers.
//
// Oct 2026 (Slate & Fire map): the "Territory washes" switch is gone with the washes themselves — the coloured diamonds
// under every keep. A stored value from before still carries `washes`; reading it keeps the switches the map still has and
// rewrites the stored value without the retired key, so an old setting can never bring the diamonds back.

export interface Layers {
  grid: boolean;
  forest: boolean;
  /** every city's nameplate when zoomed in (your own at any zoom); off = no nameplates, not even the selected city's */
  allNames: boolean;
  marches: boolean;
  /** the walking squads on the marches (Oct 15 2026); off = every march keeps its arrow (and no ground glow), the routes,
   *  ends and countdowns as before — a quieter map, and less drawing on an older phone */
  armies: boolean;
  /** Valor Tools' guild claims and op targets (Oct 15 2026; only drawn for players Valor Tools switched Valor Web on for) */
  guildMarks: boolean;
  /** the GUILDS ON THE MAP card in the realm view (Oct 15 2026: hideable; off = a slim Guilds pill beside Region) */
  guildLegend: boolean;
}

export const LAYERS_KEY = "valor-web:map-layers";
export const DEFAULT_LAYERS: Readonly<Layers> = { grid: true, forest: true, allNames: false, marches: true, armies: true, guildMarks: true, guildLegend: true };
const KEYS = Object.keys(DEFAULT_LAYERS) as (keyof Layers)[];

/** Only the switches the map has, each a boolean; anything else (a retired key, a wrong type) falls back to the default. */
export function sanitizeLayers(raw: unknown): Layers {
  const out: Layers = { ...DEFAULT_LAYERS };
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const k of KEYS) {
      const v = (raw as Record<string, unknown>)[k];
      if (typeof v === "boolean") out[k] = v;
    }
  }
  return out;
}

type LayerStore = Pick<Storage, "getItem" | "setItem">;

/** Read the saved switches; a value with retired or unknown keys is rewritten in its sanitised form. */
export function readLayers(store: LayerStore | null | undefined): Layers {
  let raw: string | null = null;
  try {
    raw = store?.getItem(LAYERS_KEY) ?? null;
  } catch {
    return { ...DEFAULT_LAYERS };
  }
  if (!raw) return { ...DEFAULT_LAYERS };
  let layers: Layers;
  try {
    layers = sanitizeLayers(JSON.parse(raw));
  } catch {
    layers = { ...DEFAULT_LAYERS };
  }
  const clean = JSON.stringify(layers);
  if (clean !== raw) writeLayers(store, layers);
  return layers;
}

export function writeLayers(store: LayerStore | null | undefined, layers: Layers) {
  try {
    store?.setItem(LAYERS_KEY, JSON.stringify(sanitizeLayers(layers)));
  } catch {
    /* private mode or full storage: the switches just aren't remembered */
  }
}
